'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Encounter } from '@/content/founder';
import ScrollReveal from './ScrollReveal';

const AUTO_ADVANCE_MS = 5000;

// hasImage is resolved server-side (publicFileExists uses node:fs, which can't bundle into a
// client component) and passed down already-computed.
interface EncounterWithImageFlag extends Encounter {
  hasImage: boolean;
}

export default function EncountersCarousel({ encounters }: { encounters: EncounterWithImageFlag[] }) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const featuredRef = useRef<HTMLDivElement>(null);
  const hasMultiple = encounters.length > 1;

  const goTo = useCallback(
    (i: number) => {
      setIndex(((i % encounters.length) + encounters.length) % encounters.length);
    },
    [encounters.length]
  );

  const restartTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!hasMultiple) return;
    timerRef.current = setInterval(() => setIndex((i) => (i + 1) % encounters.length), AUTO_ADVANCE_MS);
  }, [hasMultiple, encounters.length]);

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    restartTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [restartTimer]);

  const handleManual = (direction: 1 | -1) => {
    goTo(index + direction);
    restartTimer();
    // The featured card can sit right at the top edge of the viewport on mobile, cutting the
    // image off above the sticky navbar — nudge it fully into view on manual navigation.
    featuredRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const featured = encounters[index];
  if (!featured) return null;

  return (
    <div ref={featuredRef} className="scroll-mt-24">
      <ScrollReveal className="relative min-w-0 pb-16">
        <div
          aria-hidden="true"
          className="absolute inset-x-[4%] bottom-[18%] top-[6%] -rotate-3 rounded-[40px] bg-gradient-to-br from-[#CFE0FF] to-[#EAF2FF]"
        />
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[28px] border-[6px] border-white bg-[#EEF4FF] shadow-[0_40px_80px_-40px_rgba(25,90,255,0.55)]">
          {featured.hasImage ? (
            <Image src={featured.image!} alt={featured.imageAlt ?? featured.name} fill className="object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center font-NeueMontreal text-6xl font-bold text-[#BFD2FF]">
              {featured.name.charAt(0)}
            </div>
          )}
        </div>
        <div className="absolute bottom-0 left-1/2 w-[min(88%,420px)] -translate-x-1/2 rounded-2xl border border-[#E6EAF3] bg-white p-5 text-center shadow-[0_24px_50px_-24px_rgba(9,13,40,0.4)]">
          <div className="font-NeueMontreal text-xl font-bold text-[#090D28]">{featured.name}</div>
          <div className="mt-1 font-Schibsted_Grotesk text-sm text-[#596278]">{featured.role}</div>
          <div className="mt-2.5 font-Schibsted_Grotesk text-[15px] italic leading-snug text-[#1B2340]">
            &ldquo;{featured.quote}&rdquo;
          </div>
        </div>

      </ScrollReveal>

      {/* Same clickable-thumbnail-row pattern as the Press carousel, for consistency — only
          appears once there's more than one real encounter to navigate between. */}
      {hasMultiple && (
        <div className="mt-6 flex items-stretch gap-3">
          <button
            type="button"
            onClick={() => handleManual(-1)}
            aria-label="Previous encounter"
            className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-full border border-[#CFDAF2] bg-white text-[#0A1D57] transition-colors [-webkit-tap-highlight-color:transparent] hover:border-[#195AFF] hover:text-[#195AFF]"
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </button>

          <div className="flex min-w-0 flex-1 items-stretch gap-3 overflow-x-auto scroll-smooth px-0.5 pb-2 [scroll-snap-type:x_mandatory] [scrollbar-width:none]">
            {encounters.map((e, i) => (
              <button
                key={e.image ?? e.name}
                type="button"
                onClick={() => {
                  goTo(i);
                  restartTimer();
                }}
                aria-current={i === index}
                aria-label={`Show ${e.name}`}
                className={`flex w-[140px] shrink-0 flex-col overflow-hidden rounded-xl border bg-white text-left [-webkit-tap-highlight-color:transparent] [scroll-snap-align:start] ${
                  i === index ? 'border-[#195AFF] ring-2 ring-[#195AFF]/20' : 'border-[#E6EAF3]'
                }`}
              >
                <div className="relative aspect-square w-full bg-[#EEF4FF]">
                  {e.hasImage ? (
                    <Image src={e.image!} alt="" fill className="object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center font-NeueMontreal text-2xl font-bold text-[#BFD2FF]">
                      {e.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <p className="truncate font-Schibsted_Grotesk text-xs font-bold text-[#090D28]">{e.name}</p>
                </div>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => handleManual(1)}
            aria-label="Next encounter"
            className="flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-full border-[1.5px] border-[#195AFF] bg-white text-[#195AFF] transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-[#195AFF] hover:text-white"
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
